/**
 * Notification Panel Component
 * Production-ready notification dropdown for admin dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Badge,
  Divider,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Circle as UnreadIcon,
  Delete as DeleteIcon,
  DoneAll as MarkAllReadIcon,
  DeleteSweep as ClearAllIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Notification, NotificationPriority, NotificationType } from '../../types/notification';
import { notificationService } from '../../services/notificationService';
import { logger } from '../../services/loggerService';
import { useNavigate } from 'react-router-dom';

const NotificationPanel: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Load notifications and set up real-time subscription
  useEffect(() => {
    setLoading(true);
    
    // Subscribe to real-time updates
    const unsubscribe = notificationService.subscribeToNotifications(
      (updatedNotifications) => {
        setNotifications(updatedNotifications);
        setUnreadCount(updatedNotifications.filter((n) => !n.isRead).length);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark as read
      if (!notification.isRead) {
        await notificationService.markAsRead(notification.id);
      }

      // Navigate to action URL if available
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
        handleMenuClose();
      }
    } catch (error) {
      logger.error('Error handling notification click', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      logger.info('All notifications marked as read');
    } catch (error) {
      logger.error('Error marking all as read', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationService.deleteAllRead();
      logger.info('All read notifications cleared');
    } catch (error) {
      logger.error('Error clearing notifications', error);
    }
  };

  const handleDeleteNotification = async (
    event: React.MouseEvent,
    notificationId: string
  ) => {
    event.stopPropagation();
    try {
      await notificationService.deleteNotification(notificationId);
      logger.info('Notification deleted', { notificationId });
    } catch (error) {
      logger.error('Error deleting notification', error);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const freshNotifications = await notificationService.getNotifications();
      setNotifications(freshNotifications);
      setUnreadCount(freshNotifications.filter((n) => !n.isRead).length);
    } catch (error) {
      logger.error('Error refreshing notifications', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.URGENT:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.MEDIUM:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type: NotificationType) => {
    // Return appropriate icon based on notification type
    return '📬'; // Placeholder, can be enhanced with specific icons
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const isOpen = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="large"
        aria-label="show notifications"
        color="inherit"
        onClick={handleMenuOpen}
        sx={{
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            width: 400,
            maxHeight: 600,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
            {unreadCount > 0 && (
              <IconButton size="small" onClick={handleMarkAllRead} title="Mark all as read">
                <MarkAllReadIcon fontSize="small" />
              </IconButton>
            )}
            {notifications.some((n) => n.isRead) && (
              <IconButton size="small" onClick={handleClearAll} title="Clear read notifications">
                <ClearAllIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Notifications List */}
        <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!loading && notifications.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          )}

          {!loading && notifications.length > 0 && (
            <List sx={{ p: 0 }}>
              {notifications.map((notification) => (
                <React.Fragment key={notification.id}>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                      '&:hover': {
                        bgcolor: notification.isRead ? 'action.hover' : 'action.selected',
                      },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          {!notification.isRead && (
                            <UnreadIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                          )}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: notification.isRead ? 400 : 600,
                              flex: 1,
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Chip
                            label={notification.priority}
                            size="small"
                            color={getPriorityColor(notification.priority) as any}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                          sx={{ ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          mb: 0.5,
                        }}
                      >
                        {notification.message}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        {formatTime(notification.createdAt)}
                      </Typography>
                    </Box>
                  </ListItemButton>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button
                size="small"
                onClick={() => {
                  navigate('/notifications');
                  handleMenuClose();
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationPanel;
